import React, { useState } from "react";
import DashboardCardWrapper from "../../common/DashboardCardWrapper";
import DashboardCardHeader from "../../common/DashboardCardHeader";
import BorderedIconWrapper from "../../common/BorderIconWrapper";
import { CardContent } from "../../ui/card";
import MedicationFillHistoryLightIcon from "@/assets/svg/medicationFillHistoryLightIcon.svg";
import { DataTable } from "@/components/common/Table";
import { routes } from "@/utils/urls";
import { RootState } from "@/store/store";
import { useSelector } from "react-redux";
import MedicationFillHistoryDarkIcon from "@/assets/svg/medicationFillHistoryDarkIcon.svg";
// error handling uses unknown types
import {
  MedicationFillHistory,
  MedicationFillHistoryResponse,
} from "./medicationFillHistory";
import { columns } from "./medicationFillHistoryColumn";
import { Typography } from "@/components/ui/typography";
import { useDashboardCardFavorite } from "@/hooks/useDashboardCardFavorite";
import { Button } from "@/components/ui/button";
import MedicationFillHistoryDialog from "./MedicationFillHistoryDailog";
import { useFetchCard } from "@/hooks/useFetchCard";

interface MedicationFillHistoryCardProps {
  shouldBlink?: boolean;
}

const MedicationFillHistoryCard: React.FC<MedicationFillHistoryCardProps> = ({ shouldBlink = false }) => {
  const {
    cardVariant,
    isFavourite,
    toggleFavourite,
  } = useDashboardCardFavorite("MEDICATION_FILL_HISTORY");
  const [dialogOpen, setDialogOpen] = useState(false);

  const memberId =
    useSelector((state: RootState) => state.search.selectedPatient?.memberId) ||
    "";

  const { data: medicationFillHistoryData, loading, error, isErrorRed } = useFetchCard<MedicationFillHistory[]>({
    endpoint: `${routes.cards.medicationFillHistory('').replace(/\/$/, '')}`,
    memberId,
    method: 'POST',
    requestBody: { view: "card" },
    processData: (rawData: unknown) => (rawData as MedicationFillHistoryResponse).data || [],
  });

  const handleViewAll = () => {
    setDialogOpen(true);
  };

  return (
    <>
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md h-fit'>
        <DashboardCardWrapper variant={cardVariant} shouldBlink={shouldBlink}>
          <DashboardCardHeader
            icon={
              <BorderedIconWrapper>
                <img
                  src={MedicationFillHistoryLightIcon}
                  alt='MedicationFillHistoryLightIcon'
                  className='w-4 h-4 block dark:hidden'
                />
                <img
                  src={MedicationFillHistoryDarkIcon}
                  alt='MedicationFillHistoryDarkIcon'
                  className='w-4 h-4 hidden dark:block'
                />
              </BorderedIconWrapper>
            }
            title='Medication Fill History'
            className='h-[56px]'
            showStar={true}
            isFavourite={isFavourite}
            onToggleFavourite={toggleFavourite}
            button={
              <Button
                variant='button'
                className='!h-[28px] !px-3 !py-1 !text-sm mb-[2px]'
                onClick={handleViewAll}
                disabled={
                  loading || !!error || !medicationFillHistoryData || medicationFillHistoryData.length === 0
                }>
                View all
              </Button>
            }
          />

          <CardContent className='p-4 lg:h-[483px] '>
            {error ? (
              <div className='flex items-center justify-center h-full lg:h-[483px] md:h-[483px]'>
                  <Typography
                    variant='medium-14'
                    className={`${
                      isErrorRed
                        ? "text-red-500 dark:text-red-400"
                        : "text-grey-600 dark:text-grey-500"
                    } font-medium`}>
                    {error}
                  </Typography>
              </div>
            ) : (
              <DataTable<MedicationFillHistory>
                data={{
                  data: medicationFillHistoryData || [],
                  total: medicationFillHistoryData?.length || 0,
                }}
                columns={columns}
                height='auto'
                maxHeight={"451px"}
                isLoading={loading}
                skeletonCount={10}
              />
            )}
          </CardContent>
        </DashboardCardWrapper>
      </div>
      <MedicationFillHistoryDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        memberId={memberId}
      />
    </>
  );
};

export default MedicationFillHistoryCard;
